import React, { useState } from 'react';
import { Volume2, Play, Pause } from 'lucide-react';

const DogCommandSoundboard = () => {
  const [currentPlaying, setCurrentPlaying] = useState(null);

  const commands = [
    { polish: 'Siadaj', english: 'Sit', pronunciation: 'SHAH-die' },
    { polish: 'Zostań', english: 'Stay', pronunciation: 'ZOS-tahn' },
    { polish: 'Chodź', english: 'Come', pronunciation: 'HODJE' },
    { polish: 'Leżeć', english: 'Down/Lie down', pronunciation: 'LEH-zhetch' },
    { polish: 'Poczekaj', english: 'Wait', pronunciation: 'po-CHEH-kai' },
    { polish: 'Nie', english: 'No', pronunciation: 'NYEH' },
    { polish: 'Dobrze', english: 'Good', pronunciation: 'DOB-zheh' },
    { polish: 'Daj łapę', english: 'Give paw', pronunciation: 'dai WAH-peh' },
    { polish: 'Do domu', english: 'Go home', pronunciation: 'do DO-moo' },
    { polish: 'Spokój', english: 'Calm/Quiet', pronunciation: 'SPO-kooi' },
    { polish: 'Idź', english: 'Go', pronunciation: 'EEJE' },
    { polish: 'Stop', english: 'Stop', pronunciation: 'STOP' }
  ];

  const speakCommand = (text, index) => {
    if (currentPlaying === index) {
      speechSynthesis.cancel();
      setCurrentPlaying(null);
      return;
    }

    speechSynthesis.cancel();
    
    // Get available voices and find Polish voice
    const voices = speechSynthesis.getVoices();
    const polishVoice = voices.find(voice => 
      voice.lang.startsWith('pl') || 
      voice.lang === 'pl-PL'
    );
    
    const utterance = new SpeechSynthesisUtterance(text);
    
    // Set Polish voice if available, otherwise use default
    if (polishVoice) {
      utterance.voice = polishVoice;
    }
    
    utterance.lang = 'pl-PL';
    utterance.rate = 0.7;
    utterance.pitch = 1;
    
    utterance.onstart = () => setCurrentPlaying(index);
    utterance.onend = () => setCurrentPlaying(null);
    utterance.onerror = () => setCurrentPlaying(null);
    
    speechSynthesis.speak(utterance);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            🐕 Dog Commands Soundboard
          </h1>
          <p className="text-gray-600 text-lg">
            Polish commands for your dog
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Click on a command to hear the correct pronunciation
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {commands.map((command, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              <div className="p-6">
                <div className="text-center mb-4">
                  <h3 className="text-2xl font-bold text-indigo-600 mb-1">
                    {command.polish}
                  </h3>
                  <p className="text-gray-600 font-medium">
                    {command.english}
                  </p>
                  <p className="text-sm text-gray-500 italic">
                    {command.pronunciation}
                  </p>
                </div>
                
                <button
                  onClick={() => speakCommand(command.polish, index)}
                  className={`w-full py-3 px-4 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center space-x-2 ${
                    currentPlaying === index
                      ? 'bg-red-500 hover:bg-red-600 text-white'
                      : 'bg-indigo-500 hover:bg-indigo-600 text-white'
                  }`}
                >
                  {currentPlaying === index ? (
                    <>
                      <Pause size={20} />
                      <span>Stop</span>
                    </>
                  ) : (
                    <>
                      <Volume2 size={20} />
                      <span>Listen</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
            💡 Usage Tips
          </h2>
          <div className="space-y-2 text-gray-600">
            <p>• Use a firm but kind tone</p>
            <p>• Reward immediately when the dog obeys</p>
            <p>• Repeat the command clearly if needed</p>
            <p>• Consistency is key - always use the same words</p>
          </div>
        </div>

        <div className="mt-6 text-center text-gray-500 text-sm">
          <p>🎵 Use headphones or speakers for better audio quality</p>
        </div>
      </div>
    </div>
  );
};

export default DogCommandSoundboard;
